/**
 * PricingPage - Clear pricing tiers and comparison
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Check, X, Zap, Crown, Building2, 
  Sparkles, Image, Video, MessageSquare, Code
} from 'lucide-react';

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = [
    {
      name: "Free",
      description: "Try before you buy",
      price: 0,
      period: "forever",
      highlight: false,
      features: [
        { text: "5 free tasks", included: true },
        { text: "All task templates", included: true },
        { text: "1 custom chatbot", included: true },
        { text: "Basic support", included: true },
        { text: "Premium templates", included: false },
        { text: "Priority processing", included: false },
      ],
      cta: "Get Started",
      ctaAction: () => navigate('/auth?mode=register')
    },
    {
      name: "Pay as You Go",
      description: "Only pay for what you use",
      price: null,
      priceText: "From $0.50",
      period: "per task",
      highlight: true,
      badge: "POPULAR",
      features: [
        { text: "Unlimited tasks", included: true },
        { text: "All task templates", included: true },
        { text: "3 custom chatbots", included: true },
        { text: "Image generation ($2)", included: true },
        { text: "Video generation ($5)", included: true },
        { text: "Email support", included: true },
      ],
      cta: "Start Now",
      ctaAction: () => navigate('/templates')
    },
    {
      name: "Premium",
      description: "For power users",
      price: billingCycle === 'monthly' ? 29 : 290,
      period: billingCycle === 'monthly' ? '/month' : '/year',
      highlight: false,
      badge: billingCycle === 'yearly' ? 'SAVE 17%' : null,
      features: [
        { text: "Everything in Pay as You Go", included: true },
        { text: "20% discount on all tasks", included: true },
        { text: "Unlimited chatbots", included: true },
        { text: "Priority processing", included: true },
        { text: "Premium templates", included: true },
        { text: "Priority support", included: true },
      ],
      cta: "Go Premium",
      ctaAction: () => navigate('/subscription')
    },
    {
      name: "Enterprise",
      description: "For teams & businesses",
      price: null,
      priceText: "Custom",
      period: "pricing",
      highlight: false,
      features: [
        { text: "Everything in Premium", included: true },
        { text: "White-label solution", included: true },
        { text: "API access", included: true },
        { text: "Custom integrations", included: true },
        { text: "Dedicated support", included: true },
        { text: "SLA guarantee", included: true },
      ],
      cta: "Contact Sales",
      ctaAction: () => window.location.href = 'mailto:sales@tripletc.com'
    }
  ];

  const taskPricing = [
    { icon: MessageSquare, name: "Text Tasks", price: "$0.50", desc: "Content, emails, research" },
    { icon: Code, name: "Code Tasks", price: "$0.50", desc: "Code review, generation" },
    { icon: Image, name: "Image Generation", price: "$2.00", desc: "AI images, logos, mockups" },
    { icon: Video, name: "Video Generation", price: "$5.00", desc: "Sora 2 videos (4-12s)" },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Start free, pay only for what you use. No hidden fees.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <span className={`text-sm ${billingCycle === 'monthly' ? 'text-white' : 'text-zinc-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                billingCycle === 'yearly' ? 'bg-teal-500' : 'bg-zinc-700'
              }`}
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                billingCycle === 'yearly' ? 'translate-x-8' : 'translate-x-1'
              }`} />
            </button>
            <span className={`text-sm ${billingCycle === 'yearly' ? 'text-white' : 'text-zinc-500'}`}>
              Yearly <span className="text-teal-400">(Save 17%)</span>
            </span>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative rounded-2xl p-6 ${
                plan.highlight 
                  ? 'bg-gradient-to-b from-teal-500/20 to-zinc-900 border-2 border-teal-500/50' 
                  : 'bg-zinc-900/50 border border-white/10'
              }`}
            >
              {plan.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {plan.badge}
                </span>
              )}

              <div className="mb-6">
                <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                <p className="text-zinc-400 text-sm">{plan.description}</p>
              </div>

              <div className="mb-6">
                {plan.price !== null ? (
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">${plan.price}</span>
                    <span className="text-zinc-400">{plan.period}</span>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">{plan.priceText}</span>
                    <span className="text-zinc-400">{plan.period}</span>
                  </div>
                )}
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2">
                    {feature.included ? (
                      <Check className="w-4 h-4 text-teal-400 flex-shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                    )}
                    <span className={feature.included ? 'text-zinc-300' : 'text-zinc-600'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={plan.ctaAction}
                className={`w-full ${
                  plan.highlight
                    ? 'bg-teal-500 hover:bg-teal-600'
                    : 'bg-zinc-800 hover:bg-zinc-700'
                }`}
              >
                {plan.cta}
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Task Pricing Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8"
        >
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            Task Pricing Breakdown
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {taskPricing.map((task, index) => (
              <div key={index} className="bg-zinc-800/50 rounded-xl p-4 text-center">
                <div className="w-12 h-12 bg-zinc-700/50 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <task.icon className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-white font-semibold mb-1">{task.name}</h3>
                <p className="text-2xl font-bold text-teal-400 mb-1">{task.price}</p>
                <p className="text-zinc-500 text-sm">{task.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-zinc-400 mt-6">
            Premium subscribers get <span className="text-teal-400 font-semibold">20% off</span> all task prices
          </p>
        </motion.div>

        {/* FAQ Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-zinc-400">
            Have questions? Check our{' '}
            <a href="/faq" className="text-teal-400 hover:underline">FAQ</a>
            {' '}or{' '}
            <a href="mailto:support@tripletc.com" className="text-teal-400 hover:underline">contact support</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
